import { useHydrated } from "@tanstack/react-router";
import { useNow } from "@softmax/web-features/time/clock";
import type { LiveState } from "./types";
import s from "./onepager.module.css";
function span(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return m >= 60
    ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`
    : `${m}:${String(sec).padStart(2, "0")}`;
}
/** "12 min ago", "3 h ago": how long since a round finished, from the browser's clock. */
function ago({
  at,
  now,
}: {
  readonly at: string;
  readonly now: number | null;
}): string | null {
  if (now === null) return null;

  const min = Math.max(0, Math.round((now - new Date(at).getTime()) / 60e3));

  if (min < 1) return "just now";

  if (min < 60) return `${min} min ago`;

  const h = Math.round(min / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
}
function finished({
  prev,
  now,
}: {
  readonly prev: {
    readonly round: number;
    readonly at: string;
  } | null;
  readonly now: number | null;
}): string | null {
  const when = prev
    ? ago({
        at: prev.at,
        now,
      })
    : null;
  return prev && when ? `Round ${prev.round} finished ${when}` : null;
}
/** What's live: the round being played now, or the countdown to the next. */
export function LiveClock({ live }: { readonly live: LiveState }) {
  const clock = useNow(
    live.kind === "unknown" || live.kind === "running" ? null : 1000,
  );
  const hydrated = useHydrated();
  const now = hydrated ? clock : null;

  if (live.kind === "unknown")
    return (
      <aside className={s.live} aria-label="What's live">
        <span className={s.liveLabel}>What&apos;s live</span>
        <span className={s.liveBig}>No round yet</span>
      </aside>
    );

  if (live.kind === "queued")
    return (
      <aside className={s.live} aria-label="What's live" data-kind={live.kind}>
        <span className={s.liveLabel}>Next round</span>
        <span className={s.liveBig}>{`Round ${live.round}`}</span>
        <span className={s.liveSmall}>
          {[
            finished({
              prev: live.prev,
              now,
            }),
            "waiting to start",
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </aside>
    );

  const view = roundView({ live, now });
  return (
    <aside className={s.live} aria-label="What's live" data-kind={live.kind}>
      <span className={s.liveLabel}>
        {view.isLive ? (
          <>
            <span className={s.liveDot} aria-hidden="true" /> Live now
          </>
        ) : (
          "Next round"
        )}
      </span>
      <span className={s.liveBig}>{view.primary}</span>
      <span className={s.liveSmall}>{view.detail}</span>
      {view.previous ? (
        <span className={s.liveSmall}>{view.previous}</span>
      ) : null}
    </aside>
  );
}

function roundView({
  live,
  now,
}: {
  readonly live: Extract<LiveState, { kind: "running" | "next" }>;
  readonly now: number | null;
}) {
  if (live.kind === "running")
    return {
      isLive: true,
      primary: `Round ${live.round}`,
      detail: "being played",
      previous: null,
    };

  const due = new Date(live.at).getTime();
  const overdue = now !== null && now >= due;
  return {
    isLive: overdue,
    primary: overdue
      ? `Round ${live.round}`
      : now === null
        ? "…"
        : span(due - now),
    detail: overdue ? "starting" : `until round ${live.round}`,
    previous: overdue ? null : finished({ prev: live.prev, now }),
  };
}
